/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect } from 'react';

import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';

import { ContractTermInfoButton } from 'src/components/ContractTermInfoButton';
import { Input } from 'src/components/Input';
import { multiplierInputValidationRules } from 'src/components/RateCardDetail/ContractTerms/contractTermValidation';
import { ContractTermName } from 'src/constants/contractTermHelpInfo';
import { IContractTermsComponentProps } from 'src/types/IRateCard';
import { hasErrorMessage } from 'src/util/validationsUtil';

export const RateMultipliers: React.FC<IContractTermsComponentProps> = ({
    contractTerms,
    handleContractTermValueChange,
    setIsContractTermInputValid,
    isEditMode,
    contractTermErrorMessages,
    handleContractTermErrorMessageChange
}: IContractTermsComponentProps) => {
    const handleSingleErrorMsgChange = (field: string, value: string) => {
        if (!handleContractTermErrorMessageChange) return;
        handleContractTermErrorMessageChange('rateMultipliers', field, value);
        if (!setIsContractTermInputValid) return;
    };

    useEffect(() => {
        if (!setIsContractTermInputValid) return;
        setIsContractTermInputValid((prevState) => ({
            ...prevState,
            rateMultipliers: !hasErrorMessage(contractTermErrorMessages!.rateMultipliers)
        }));
    }, [contractTermErrorMessages!.rateMultipliers]);

    return (
        <Box className="box-border-light-gray" type="outline" spacingInset="400">
            <Column>
                <Row spacing="none">
                    <Heading level={3}>Rate multipliers</Heading>
                    <ContractTermInfoButton contractTermName={ContractTermName.RateMultipliers} />
                </Row>
                <Row widths="20%" alignmentVertical="top">
                    <Input
                        label="Daily"
                        value={(contractTerms.rateMultipliers?.daily ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) => handleContractTermValueChange('rateMultipliers', 'daily', newVal)}
                        errorMessage={contractTermErrorMessages!.rateMultipliers.daily}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('daily', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                    <Input
                        label="Weekly"
                        value={(contractTerms.rateMultipliers?.weekly ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) => handleContractTermValueChange('rateMultipliers', 'weekly', newVal)}
                        errorMessage={contractTermErrorMessages!.rateMultipliers.weekly}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('weekly', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                    <Input
                        label="Annual"
                        value={(contractTerms.rateMultipliers?.annual ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) => handleContractTermValueChange('rateMultipliers', 'annual', newVal)}
                        errorMessage={contractTermErrorMessages!.rateMultipliers.annual}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('annual', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                </Row>
                <Row widths="20%" alignmentVertical="top">
                    <Input
                        label="3m price drop"
                        value={(contractTerms.rateMultipliers?.priceDrop3m ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) => handleContractTermValueChange('rateMultipliers', 'priceDrop3m', newVal)}
                        errorMessage={contractTermErrorMessages!.rateMultipliers.priceDrop3m}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('priceDrop3m', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                    <Input
                        label="4m price drop"
                        value={(contractTerms.rateMultipliers?.priceDrop4m ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) => handleContractTermValueChange('rateMultipliers', 'priceDrop4m', newVal)}
                        errorMessage={contractTermErrorMessages!.rateMultipliers.priceDrop4m}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('priceDrop4m', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                    <Input
                        label="6m price drop"
                        value={(contractTerms.rateMultipliers?.priceDrop6m ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) => handleContractTermValueChange('rateMultipliers', 'priceDrop6m', newVal)}
                        errorMessage={contractTermErrorMessages!.rateMultipliers.priceDrop6m}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('priceDrop6m', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                    <Input
                        label="Winback"
                        value={(contractTerms.rateMultipliers?.winback ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) => handleContractTermValueChange('rateMultipliers', 'winback', newVal)}
                        errorMessage={contractTermErrorMessages!.rateMultipliers.winback}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('winback', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                </Row>
                <Row widths="20%" alignmentVertical="top">
                    <Input
                        label="Ext trial 2m"
                        value={(contractTerms.rateMultipliers?.extendedTrial2m ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) =>
                            handleContractTermValueChange('rateMultipliers', 'extendedTrial2m', newVal)
                        }
                        errorMessage={contractTermErrorMessages!.rateMultipliers.extendedTrial2m}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('extendedTrial2m', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                    <Input
                        label="Ext trial 3m"
                        value={(contractTerms.rateMultipliers?.extendedTrial3m ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) =>
                            handleContractTermValueChange('rateMultipliers', 'extendedTrial3m', newVal)
                        }
                        errorMessage={contractTermErrorMessages!.rateMultipliers.extendedTrial3m}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('extendedTrial3m', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                    <Input
                        label="Ext trial 4m"
                        value={(contractTerms.rateMultipliers?.extendedTrial4m ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) =>
                            handleContractTermValueChange('rateMultipliers', 'extendedTrial4m', newVal)
                        }
                        errorMessage={contractTermErrorMessages!.rateMultipliers.extendedTrial4m}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('extendedTrial4m', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                    <Input
                        label="Ext trial 5m"
                        value={(contractTerms.rateMultipliers?.extendedTrial5m ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) =>
                            handleContractTermValueChange('rateMultipliers', 'extendedTrial5m', newVal)
                        }
                        errorMessage={contractTermErrorMessages!.rateMultipliers.extendedTrial5m}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('extendedTrial5m', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                    <Input
                        label="Ext trial 6m"
                        value={(contractTerms.rateMultipliers?.extendedTrial6m ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) =>
                            handleContractTermValueChange('rateMultipliers', 'extendedTrial6m', newVal)
                        }
                        errorMessage={contractTermErrorMessages!.rateMultipliers.extendedTrial6m}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('extendedTrial6m', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                </Row>
                <Row widths="20%" alignmentVertical="top">
                    <Input
                        label="ASD ext trial 2m"
                        value={(contractTerms.rateMultipliers?.asdExtendedTrial2m ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) =>
                            handleContractTermValueChange('rateMultipliers', 'asdExtendedTrial2m', newVal)
                        }
                        errorMessage={contractTermErrorMessages!.rateMultipliers.asdExtendedTrial2m}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('asdExtendedTrial2m', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                    <Input
                        label="ASD ext trial 3m"
                        value={(contractTerms.rateMultipliers?.asdExtendedTrial3m ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) =>
                            handleContractTermValueChange('rateMultipliers', 'asdExtendedTrial3m', newVal)
                        }
                        errorMessage={contractTermErrorMessages!.rateMultipliers.asdExtendedTrial3m}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('asdExtendedTrial3m', newVal)}
                        validationRules={multiplierInputValidationRules}
                        type="number"
                    />
                </Row>
            </Column>
        </Box>
    );
};
